/**
 * Маршруты и первое, что видит человек.
 *
 * Раньше пустое локальное состояние сразу открывало анкету, а проверка сессии
 * шла отдельно и в это решение не входила. Поэтому новый браузер не получал
 * ни объяснения продукта, ни явного входа, а вернувшийся человек мог увидеть
 * пустой онбординг раньше, чем загрузятся его данные.
 *
 * Здесь состояние запуска различает четыре вещи, а не сводит всё к
 * `user === null`: сессия проверяется · гость · вошёл · проверка недоступна.
 *
 * Пятое различие появилось из установленного приложения. У приложения на
 * домашнем экране своё хранилище: сессия из браузера может достаться ему, а
 * данные — нет. Вошедший человек с пустым устройством попадал в анкету и
 * заполнял заново то, что уже лежит в его аккаунте. Поэтому данные сперва
 * восстанавливаются с сервера, и всё это время показывается загрузка.
 */

export type Route =
  /** Сессия ещё проверяется: показываем загрузку, а не пустой экран. */
  | 'loading'
  | 'welcome'
  | 'login'
  | 'onboarding'
  | 'app'
  | 'plans'
  /** Создание аккаунта после первого разбора — отдельный шаг от старта. */
  | 'signup'
  /** Новый пароль по ссылке из письма. */
  | 'reset';

export type SessionState = 'checking' | 'guest' | 'user' | 'unavailable';

export interface RouteInput {
  /** Адрес без домена: «/», «/login», «/plans». */
  path: string;
  session: SessionState;
  /** Есть рабочие данные на устройстве: счета, операции, план. */
  hasWorkingData: boolean;
  /** Есть незавершённый черновик настройки. */
  hasDraft: boolean;
  /**
   * Данные аккаунта ещё едут с сервера.
   *
   * Так выглядит запуск приложения, установленного на телефон: сессия на месте,
   * а хранилище у установленного приложения своё и пустое. Показать анкету в
   * этот момент — предложить человеку с готовой картиной завести её заново.
   */
  restoring?: boolean;
}

const PATHS: Record<string, Route> = {
  '/': 'welcome',
  '/login': 'login',
  '/register': 'onboarding',
  '/onboarding': 'onboarding',
  '/app': 'app',
  '/plans': 'plans',
  '/signup': 'signup',
  '/reset': 'reset',
};

/**
 * Какой экран показать.
 *
 * Порядок правил важен: сначала то, что нельзя показывать неправильно.
 */
export function resolveRoute(input: RouteInput): Route {
  const asked = PATHS[normalize(input.path)] ?? 'welcome';

  // Сравнение тарифов доступно всегда и ничего не запускает.
  if (asked === 'plans') return 'plans';
  // Ссылка из письма открывается всегда: ни данные на устройстве, ни чужая
  // сессия не должны уводить человека с замены пароля.
  if (asked === 'reset') return 'reset';

  // Пока сессия проверяется, показывать нельзя ни приветствие, ни анкету:
  // человек с аккаунтом увидел бы чужой экран на долю секунды.
  if (input.session === 'checking' && !input.hasWorkingData) return 'loading';

  // Рабочие данные на устройстве — сразу главная. Маркетинговое приветствие
  // вернувшемуся человеку показывать незачем.
  if (input.hasWorkingData) return asked === 'login' || asked === 'signup' ? asked : 'app';

  // Данные аккаунта ещё не пришли: ждём их, а не спрашиваем заново.
  if (input.restoring) return 'loading';

  // Незавершённый черновик продолжается, а не начинается заново.
  if (input.hasDraft) return asked === 'login' ? 'login' : 'onboarding';

  // Вошёл, но данных нет: настройка внутри аккаунта, без повторной регистрации.
  if (input.session === 'user') return asked === 'welcome' ? 'onboarding' : asked;

  // Проверка сессии недоступна и локальных данных нет: показываем старт,
  // но вход остаётся доступным — он объяснит, что нужна сеть.
  return asked === 'app' ? 'welcome' : asked;
}

/** Убирает хвостовой слеш и параметры: «/app/?x=1» → «/app». */
export function normalize(path: string): string {
  const clean = path.split('?')[0]!.split('#')[0]!;
  if (clean.length > 1 && clean.endsWith('/')) return clean.slice(0, -1);
  return clean === '' ? '/' : clean;
}

/** Адрес для экрана: обратное отображение, для перехода после действия. */
export function pathOf(route: Route): string {
  switch (route) {
    case 'welcome': return '/';
    case 'login': return '/login';
    case 'onboarding': return '/onboarding';
    case 'app': return '/app';
    case 'plans': return '/plans';
    case 'signup': return '/signup';
    case 'reset': return '/reset';
    case 'loading': return '/';
  }
}

/** Текущий путь браузера. Вне браузера — корень. */
export const currentPath = (): string =>
  (typeof location === 'undefined' ? '/' : normalize(location.pathname));

/**
 * Переход без перезагрузки.
 *
 * Прямое открытие и обновление каждого адреса должны работать, поэтому это
 * History API, а не хеш: «/app» в адресной строке — обычная страница.
 */
export function navigate(route: Route): void {
  if (typeof history === 'undefined') return;
  const next = pathOf(route);
  if (normalize(location.pathname) !== next) history.pushState({}, '', next);
  dispatchEvent(new PopStateEvent('popstate'));
}
